import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { api, ApiFailure, type MarketingResponse } from '../api';
import type { VizColors } from '../theme';
import { Notice } from './Panels';
import { Icon } from './Icon';
import { Hint } from './Hint';
import {
  fmtMoney,
  fmtNumber,
  fmtPercent,
  fmtRatio,
  LoadingCard,
  localDate,
  PageHeader,
  RangePicker,
  readPref,
  useDateRange,
  writePref,
} from './analytics/shared';

/**
 * Marketing ROI — the profit view DTC teams buy Triple Whale and Northbeam for.
 *
 * Three reads of the same money, from least to most model-dependent:
 *
 * 1. Blended (MER, NC-ROAS, nCAC): no attribution at all, so nothing to argue.
 * 2. Attributed channel ROAS, under the model and lookback window you pick.
 * 3. Marketing-mix model: incremental revenue per channel from spend alone,
 *    which keeps working when cookies and click ids do not.
 *
 * Profit uses the cost assumptions entered on this page; they are shown next
 * to every profit figure so the number is never read without its inputs.
 */

interface Costs {
  cogs: number;
  fees: number;
  shipping: number;
  fixed: number;
}

const DEFAULT_COSTS: Costs = { cogs: 0, fees: 2.9, shipping: 0, fixed: 0 };

const MODELS: [string, string][] = [
  ['linear', 'Linear'],
  ['first_touch', 'First touch'],
  ['last_touch', 'Last touch'],
  ['time_decay', 'Time decay'],
  ['position_based', 'Position based (40/20/40)'],
  ['markov', 'Data-driven (Markov)'],
  ['shapley', 'Data-driven (Shapley)'],
];

function readCosts(): Costs {
  try {
    const raw = readPref('dv-costs');
    if (raw === null) return DEFAULT_COSTS;
    const parsed = JSON.parse(raw) as Partial<Costs>;
    return { ...DEFAULT_COSTS, ...parsed };
  } catch {
    return DEFAULT_COSTS;
  }
}

export function Marketing({ colors, onNavigate }: { colors: VizColors; onNavigate: (page: 'campaign-spend' | 'websites') => void }) {
  const { range, preset, setPreset, custom, setCustom } = useDateRange('dv-mkt-range', '90d');
  const [costs, setCosts] = useState<Costs>(readCosts);
  const [model, setModel] = useState(() => readPref('dv-mkt-model') ?? 'linear');
  const [windowDays, setWindowDays] = useState(() => Number(readPref('dv-mkt-window') ?? 30) || 30);
  const [data, setData] = useState<MarketingResponse | null>(null);
  const [failure, setFailure] = useState<ApiFailure | null>(null);
  const [loading, setLoading] = useState(false);
  const [showCosts, setShowCosts] = useState(false);
  const token = useRef(0);

  const startDate = localDate(new Date(range.start));
  const endDate = localDate(new Date(range.end));

  const load = useCallback(async () => {
    const mine = ++token.current;
    setLoading(true);
    setFailure(null);
    try {
      const response = await api.getMarketing({ startDate, endDate, ...costs, model, window: windowDays });
      if (mine === token.current) setData(response);
    } catch (error) {
      if (mine === token.current) setFailure(error instanceof ApiFailure ? error : new ApiFailure('Request failed.', 'Try again.', 0));
    } finally {
      if (mine === token.current) setLoading(false);
    }
  }, [startDate, endDate, costs, model, windowDays]);

  useEffect(() => {
    const id = window.setTimeout(() => void load(), 250); // debounce cost typing
    return () => window.clearTimeout(id);
  }, [load]);

  const updateCost = (key: keyof Costs, value: string) => {
    const n = Math.max(0, Number(value) || 0);
    const next = { ...costs, [key]: key === 'cogs' || key === 'fees' ? Math.min(100, n) : n };
    setCosts(next);
    writePref('dv-costs', JSON.stringify(next));
  };

  const s = data?.marketing.summary ?? null;
  const empty = s !== null && s.grossRevenue === 0 && s.adSpend === 0;

  return (
    <div className="page an-page">
      <PageHeader title="Marketing ROI" subtitle="Blended efficiency, profit after costs, channel ROAS and incremental lift — in one place.">
        <RangePicker
          preset={preset}
          onPreset={setPreset}
          custom={custom}
          onCustom={setCustom}
          presets={['7d', '30d', 'mtd', 'lastMonth', '90d', '12mo', 'custom']}
        />
        <button className={showCosts ? 'ghost on' : 'ghost'} onClick={() => setShowCosts(!showCosts)} aria-expanded={showCosts}>
          <Icon name="settings" size={15} /> Costs
        </button>
      </PageHeader>

      {showCosts && (
        <section className="card cost-card">
          <header>
            <h2>Cost assumptions</h2>
            <span className="sub">used for every profit figure on this page · saved in this browser</span>
          </header>
          <div className="cost-grid">
            <CostField label="Cost of goods" suffix="% of revenue" value={costs.cogs} onChange={(v) => updateCost('cogs', v)}
              hint="Product cost as a share of the selling price. A SaaS product is usually 5–20% (hosting, support)." />
            <CostField label="Payment fees" suffix="% of revenue" value={costs.fees} onChange={(v) => updateCost('fees', v)}
              hint="Stripe is ~2.9% + 30¢ in the US; enter the percentage part." />
            <CostField label="Shipping & fulfilment" suffix="per order" value={costs.shipping} onChange={(v) => updateCost('shipping', v)}
              hint="Average cost to pick, pack and ship one order. Zero for digital products." />
            <CostField label="Fixed costs" suffix="per day" value={costs.fixed} onChange={(v) => updateCost('fixed', v)}
              hint="Tools, salaries and rent divided by days, if you want net profit rather than contribution." />
          </div>
        </section>
      )}

      {failure !== null && <Notice tone="error" message={failure.message} remedy={failure.remedy} />}

      {s === null ? (
        <LoadingCard label="Joining revenue, spend and journeys…" />
      ) : (
        <>
          {empty && (
            <section className="install-hint">
              <div>
                <strong>No revenue or ad spend in this period yet</strong>
                <p>
                  Revenue arrives from Stripe, LemonSqueezy, Polar or Shopify webhooks (or a CSV import). Spend is
                  entered per campaign or imported from Meta Ads. Both appear here as soon as they land.
                </p>
              </div>
              <div className="row tight">
                <button className="ghost" onClick={() => onNavigate('websites')}>Connect payments</button>
                <button className="primary" onClick={() => onNavigate('campaign-spend')}>Add ad spend</button>
              </div>
            </section>
          )}

          <section className={loading ? 'kpi-strip is-loading' : 'kpi-strip'} aria-label="Blended metrics">
            <Tile label="Net revenue" value={fmtMoney(s.netRevenue, { compact: true })} foot={`${fmtNumber(s.orders)} orders · AOV ${fmtMoney(s.averageOrderValue)}`} />
            <Tile label="Ad spend" value={fmtMoney(s.adSpend, { compact: true })} foot={s.adSpendShare === null ? '—' : `${fmtPercent(s.adSpendShare)} of revenue`} />
            <Tile label="MER" value={fmtRatio(s.mer)} foot="net revenue ÷ ad spend"
              hint="Marketing efficiency ratio. Needs no attribution model, which is why finance teams trust it."
              tone={s.mer !== null && s.breakEvenMer !== null ? (s.mer >= s.breakEvenMer ? 'good' : 'bad') : undefined} />
            <Tile label="NC-ROAS" value={fmtRatio(s.ncRoas)} foot="first-order revenue ÷ spend"
              hint="New-customer ROAS: how much first-purchase revenue each ad dollar brings in. Excludes repeat buyers your ads did not acquire." />
            <Tile label="nCAC" value={fmtMoney(s.ncac)} foot={`${fmtNumber(s.newCustomers)} new customers`}
              hint="New-customer acquisition cost: ad spend divided by first-time buyers." />
            <Tile label="Contribution profit" value={fmtMoney(s.contributionProfit, { compact: true })} foot="after COGS, fees, shipping, ads"
              tone={s.contributionProfit >= 0 ? 'good' : 'bad'} />
            <Tile label="Net profit" value={fmtMoney(s.netProfit, { compact: true })} foot={s.netMargin === null ? '—' : `${fmtPercent(s.netMargin)} margin`}
              tone={s.netProfit >= 0 ? 'good' : 'bad'} />
            <Tile label="Break-even MER" value={fmtRatio(s.breakEvenMer)} foot="below this, ads lose money"
              hint="1 ÷ (1 − variable cost share). At this MER every ad dollar exactly pays for itself after COGS and fees." />
          </section>

          <div className="two-col">
            <section className="card">
              <header>
                <h2>Revenue vs ad spend</h2>
                <span className="sub">daily · same currency axis</span>
              </header>
              <RevenueSpendChart data={data!} colors={colors} />
            </section>
            <section className="card">
              <header>
                <h2>Daily contribution profit</h2>
                <span className="sub">after variable costs and ads</span>
              </header>
              <ProfitChart data={data!} colors={colors} />
            </section>
          </div>

          <div className="two-col wide-left">
            <ChannelTable
              data={data!}
              model={model}
              onModel={(m) => { setModel(m); writePref('dv-mkt-model', m); }}
              windowDays={windowDays}
              onWindow={(w) => { setWindowDays(w); writePref('dv-mkt-window', String(w)); }}
            />
            <ProfitStatement s={s} />
          </div>

          <MixModel data={data!} />

          <p className="an-footnote">
            <Icon name="info" size={13} /> Spend entries are spread evenly over the days they cover. A customer is new on
            their first-ever order. Attribution counts touchpoints inside the lookback window before each conversion;
            the mix model reads only daily revenue and spend, so it needs no tracking at all.
          </p>
        </>
      )}
    </div>
  );
}

function CostField({ label, suffix, value, onChange, hint }: { label: string; suffix: string; value: number; onChange: (v: string) => void; hint: string }) {
  return (
    <label className="field cost-field">
      <span>
        {label} <Hint text={hint} />
      </span>
      <span className="input-suffix">
        <input type="number" min={0} step="0.1" value={value} onChange={(e) => onChange(e.target.value)} />
        <em>{suffix}</em>
      </span>
    </label>
  );
}

function Tile({ label, value, foot, hint, tone }: { label: string; value: string; foot: string; hint?: string; tone?: 'good' | 'bad' }) {
  return (
    <div className="kpi">
      <span className="kpi-label">
        {label}
        {hint !== undefined && <Hint text={hint} />}
      </span>
      <span className={tone === undefined ? 'kpi-value' : `kpi-value tone-${tone}`}>{value}</span>
      <span className="kpi-foot">{foot}</span>
    </div>
  );
}

function shortDate(iso: string): string {
  return new Date(`${iso}T12:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function RevenueSpendChart({ data, colors }: { data: MarketingResponse; colors: VizColors }) {
  const rows = data.marketing.daily.map((d) => ({ label: shortDate(d.date), revenue: d.netRevenue, spend: d.adSpend }));
  if (rows.every((r) => r.revenue === 0 && r.spend === 0)) return <div className="chart-empty">Nothing to plot in this period.</div>;
  return (
    <div className="chart-frame" style={{ height: 240 }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={rows} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
          <CartesianGrid vertical={false} stroke={colors.grid} />
          <XAxis dataKey="label" tick={{ fontSize: 12, fill: colors.muted }} tickLine={false} axisLine={{ stroke: colors.axis }} minTickGap={24} />
          <YAxis tick={{ fontSize: 12, fill: colors.muted }} tickLine={false} axisLine={false} width={60} tickFormatter={(v: number) => fmtMoney(v, { compact: true })} />
          <Tooltip
            formatter={(v: number, name: string) => [fmtMoney(v), name]}
            contentStyle={{ background: colors.surface, border: `1px solid ${colors.grid}`, borderRadius: 8, fontSize: 12 }}
          />
          <Legend verticalAlign="top" align="right" height={24} iconType="plainline" wrapperStyle={{ fontSize: 12 }} />
          <Line type="monotone" dataKey="revenue" name="Net revenue" stroke={colors.categorical[0]} strokeWidth={2} dot={false} isAnimationActive={false} />
          <Line type="monotone" dataKey="spend" name="Ad spend" stroke={colors.categorical[1]} strokeWidth={2} dot={false} isAnimationActive={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function ProfitChart({ data, colors }: { data: MarketingResponse; colors: VizColors }) {
  const rows = data.marketing.daily.map((d) => ({ label: shortDate(d.date), profit: d.contributionProfit }));
  if (rows.every((r) => r.profit === 0)) return <div className="chart-empty">No profit or loss recorded in this period.</div>;
  return (
    <div className="chart-frame" style={{ height: 240 }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={rows} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
          <CartesianGrid vertical={false} stroke={colors.grid} />
          <XAxis dataKey="label" tick={{ fontSize: 12, fill: colors.muted }} tickLine={false} axisLine={{ stroke: colors.axis }} minTickGap={24} />
          <YAxis tick={{ fontSize: 12, fill: colors.muted }} tickLine={false} axisLine={false} width={60} tickFormatter={(v: number) => fmtMoney(v, { compact: true })} />
          <ReferenceLine y={0} stroke={colors.axis} />
          <Tooltip
            cursor={{ fill: colors.band }}
            formatter={(v: number) => [fmtMoney(v), 'Contribution profit']}
            contentStyle={{ background: colors.surface, border: `1px solid ${colors.grid}`, borderRadius: 8, fontSize: 12 }}
          />
          <Bar dataKey="profit" fill={colors.series1} radius={[3, 3, 0, 0]} isAnimationActive={false} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function ChannelTable({
  data,
  model,
  onModel,
  windowDays,
  onWindow,
}: {
  data: MarketingResponse;
  model: string;
  onModel: (m: string) => void;
  windowDays: number;
  onWindow: (w: number) => void;
}) {
  const { channels, attribution } = data;
  const maxRevenue = Math.max(1, ...channels.map((c) => c.attributedRevenue));
  return (
    <section className="card">
      <header className="breakdown-head">
        <h2>
          Channel performance
          <Hint text="Revenue credited to each channel under the attribution model you choose, against the spend recorded for that channel." />
        </h2>
        <div className="row tight">
          <select value={model} onChange={(e) => onModel(e.target.value)} aria-label="Attribution model">
            {MODELS.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
          </select>
          <select value={windowDays} onChange={(e) => onWindow(Number(e.target.value))} aria-label="Lookback window">
            {[1, 7, 14, 30, 60, 90].map((d) => <option key={d} value={d}>{d}-day window</option>)}
          </select>
        </div>
      </header>
      {channels.length === 0 ? (
        <p className="muted">No attributed conversions or channel spend in this period.</p>
      ) : (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Channel</th>
                <th className="num">Spend</th>
                <th className="num">Attributed revenue</th>
                <th className="num">ROAS</th>
                <th className="num">Conversions</th>
                <th className="num">CPA</th>
              </tr>
            </thead>
            <tbody>
              {channels.map((c) => (
                <tr key={c.channel}>
                  <td><strong className="cap">{c.channel}</strong></td>
                  <td className="num">{c.spend > 0 ? fmtMoney(c.spend) : '—'}</td>
                  <td className="num">
                    <span className="inline-meter" aria-hidden><span style={{ width: `${(c.attributedRevenue / maxRevenue) * 100}%` }} /></span>
                    {fmtMoney(c.attributedRevenue)}
                  </td>
                  <td className="num">{fmtRatio(c.roas)}</td>
                  <td className="num">{c.conversions.toFixed(c.conversions % 1 === 0 ? 0 : 1)}</td>
                  <td className="num">{fmtMoney(c.cpa)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="muted small">
        {attribution.journeysAnalyzed} journeys analysed{attribution.journeysSkipped > 0 ? `, ${attribution.journeysSkipped} conversions had no touchpoint in the window` : ''}.
        {attribution.thinSample && ' Fewer than 30 journeys: data-driven models are noisy at this size.'}
      </p>
    </section>
  );
}

function ProfitStatement({ s }: { s: MarketingResponse['marketing']['summary'] }) {
  const lines: [string, number, 'in' | 'out' | 'total'][] = [
    ['Gross revenue', s.grossRevenue, 'in'],
    ['Refunds', -s.refunds, 'out'],
    ['Cost of goods', -s.cogs, 'out'],
    ['Payment fees', -s.paymentFees, 'out'],
    ['Shipping', -s.shipping, 'out'],
    ['Gross profit', s.grossProfit, 'total'],
    ['Ad spend', -s.adSpend, 'out'],
    ['Contribution profit', s.contributionProfit, 'total'],
    ['Fixed costs', -s.fixedCosts, 'out'],
    ['Net profit', s.netProfit, 'total'],
  ];
  return (
    <section className="card">
      <header>
        <h2>Profit statement</h2>
        <span className="sub">
          COGS {s.costs.cogsPercent}% · fees {s.costs.paymentFeePercent}% · {fmtMoney(s.costs.shippingPerOrder)}/order
        </span>
      </header>
      <dl className="pnl">
        {lines.map(([label, value, kind]) => (
          <div key={label} className={`pnl-row ${kind}`}>
            <dt>{label}</dt>
            <dd className={kind === 'total' ? (value >= 0 ? 'tone-good' : 'tone-bad') : undefined}>{fmtMoney(value)}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

function MixModel({ data }: { data: MarketingResponse }) {
  const mix = data.mix;
  const maxShare = useMemo(() => (mix.ok ? Math.max(1, ...mix.channels.map((c) => c.share ?? 0)) : 1), [mix]);
  return (
    <section className="card">
      <header>
        <h2>
          <Icon name="marketing" size={15} /> Incremental lift (marketing-mix model)
          <Hint text="A regression of daily revenue on each channel's carried-over spend. It estimates what each channel adds on top of baseline demand, without cookies or click tracking. It is correlational, not an experiment: vary spend between channels for cleaner estimates." />
        </h2>
        {mix.ok && <span className="sub">{mix.observations} days · fit R² {mix.r2.toFixed(2)}{mix.mape !== null ? ` · error ${fmtPercent(mix.mape, 0)}` : ''}</span>}
      </header>
      {!mix.ok ? (
        <div className="mix-refusal">
          <Icon name="info" size={16} />
          <p>{mix.reason}</p>
        </div>
      ) : (
        <>
          <div className="mix-baseline">
            <div>
              <span className="kpi-label">Baseline (would happen without ads)</span>
              <strong>{fmtMoney(mix.baseline, { compact: true })}</strong>
              <span className="muted">{fmtPercent(mix.baselineShare, 0)} of modelled revenue</span>
            </div>
            <div>
              <span className="kpi-label">Carry-over</span>
              <strong>{mix.adstockDecay === 0 ? 'None' : `${Math.round(mix.adstockDecay * 100)}% / day`}</strong>
              <span className="muted">how long an ad dollar keeps working</span>
            </div>
          </div>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Channel</th>
                  <th className="num">Spend</th>
                  <th className="num">Incremental revenue</th>
                  <th className="num">Incremental ROAS</th>
                  <th>Share of lift</th>
                </tr>
              </thead>
              <tbody>
                {mix.channels.map((c) => (
                  <tr key={c.channel}>
                    <td><strong className="cap">{c.channel}</strong></td>
                    <td className="num">{fmtMoney(c.spend)}</td>
                    <td className="num">{fmtMoney(c.contribution)}</td>
                    <td className="num">{fmtRatio(c.roi)}</td>
                    <td>
                      <span className="share-bar"><span style={{ width: `${((c.share ?? 0) / maxShare) * 100}%` }} /></span>
                      <span className="muted small">{fmtPercent(c.share, 0)}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {mix.warnings.length > 0 && (
            <ul className="mix-warnings">
              {mix.warnings.map((w) => <li key={w}>{w}</li>)}
            </ul>
          )}
        </>
      )}
    </section>
  );
}
